import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Trash2, Camera, Clock, Save, Play, CheckCircle2, ShieldCheck, Images } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import PhotoUpload from "@/components/PhotoUpload";
import BulkMediaUpload from "@/components/BulkMediaUpload";
import {
    TASK_STATE_LABELS,
    TASK_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";

export default function CleaningDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [property, setProperty] = useState(null);
    const [assignee, setAssignee] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        const { data: t } = await api.get(`/tasks/${id}`);
        setTask(t);
        if (t.property_id) {
            const { data: pd } = await api.get(`/properties/${t.property_id}`).catch(() => ({ data: null }));
            setProperty(pd?.property || null);
        }
        if (t.assigned_to) {
            const emps = (await api.get("/employees")).data || [];
            setAssignee(emps.find((e) => e.id === t.assigned_to) || null);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    if (!task) return <div className="zentra-skeleton h-80" />;

    const updateChecklistItem = (index, patch) => {
        setTask((t) => ({
            ...t,
            checklist: t.checklist.map((it, i) => (i === index ? { ...it, ...patch } : it)),
        }));
    };

    const saveChecklist = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.patch(`/tasks/${id}`, { checklist: task.checklist });
            setTask(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const setState = async (state) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/tasks/${id}`, { state, checklist: task.checklist });
            setTask(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("¿Eliminar esta tarea?")) return;
        await api.delete(`/tasks/${id}`);
        navigate("/limpiezas");
    };

    const total = task.checklist?.length || 0;
    const done = task.checklist?.filter((c) => c.done).length || 0;
    const progress = total ? Math.round((done / total) * 100) : 0;

    return (
        <div className="space-y-6" data-testid="cleaning-detail">
            <Link to="/limpiezas" className="inline-flex items-center gap-2 text-sm text-zentra-muted hover:text-zentra-ink">
                <ArrowLeft size={14} /> Volver a limpiezas
            </Link>

            <div className="zentra-card-static p-6 zentra-accent-rule pl-7">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                            {task.scheduled_date} · {property?.code || ""}
                        </p>
                        <h2 className="font-display text-2xl font-bold text-zentra-ink mt-1">
                            {TASK_TYPE_LABELS[task.type] || task.type}
                            {property?.name ? ` · ${property.name}` : ""}
                        </h2>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <StateBadge value={task.state} dict={TASK_STATE_LABELS} />
                            <StateBadge value={task.priority} dict={PRIORITY_LABELS} />
                            <span className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border">
                                <Clock size={11} className="mr-1" /> {task.estimated_minutes}' estimados
                            </span>
                            {assignee && (
                                <span className="zentra-chip bg-zentra-primary text-white border-zentra-primary">
                                    Asignado · {assignee.name}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {task.state === "pending" && (
                            <button className="zentra-btn-primary" onClick={() => setState("in_progress")} disabled={saving} data-testid="start-task">
                                <Play size={14} /> Iniciar
                            </button>
                        )}
                        {task.state === "in_progress" && (
                            <button className="zentra-btn-accent" onClick={() => setState("completed")} disabled={saving} data-testid="complete-task">
                                <CheckCircle2 size={14} /> Completar
                            </button>
                        )}
                        {task.state === "completed" && (
                            <button className="zentra-btn-accent" onClick={() => setState("validated")} disabled={saving} data-testid="validate-task">
                                <ShieldCheck size={14} /> Validar
                            </button>
                        )}
                        <button className="zentra-btn-danger" onClick={remove} data-testid="delete-task">
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>

                {task.notes && (
                    <div className="mt-4 p-3 rounded-md bg-zentra-bg/40 border border-zentra-border text-sm text-zentra-ink">
                        <strong className="text-zentra-muted text-[11px] uppercase tracking-wider block mb-1">Notas</strong>
                        {task.notes}
                    </div>
                )}

                <div className="mt-5">
                    <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-zentra-muted font-medium tracking-wider uppercase">Progreso checklist</span>
                        <span className="font-display font-semibold text-zentra-ink">
                            {done}/{total} · {progress}%
                        </span>
                    </div>
                    <div className="w-full h-2 bg-zentra-bg rounded-full overflow-hidden border border-zentra-border">
                        <div
                            className="h-full transition-all"
                            style={{ width: `${progress}%`, background: "#23C483" }}
                            data-testid="task-progress-bar"
                        />
                    </div>
                </div>
            </div>

            {/* Bulk media uploader for the office */}
            <div className="zentra-card-static p-5" data-testid="task-bulk-media">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display font-semibold text-zentra-ink inline-flex items-center gap-2">
                            <Images size={16} /> Archivos de la limpieza
                        </h3>
                        <p className="text-xs text-zentra-muted mt-0.5">
                            Subida desde oficina · {(task.media || []).length} archivos actuales
                        </p>
                    </div>
                    {task.media?.length > 0 && (
                        <Link
                            to={`/portal/limpiezas/${task.id}`}
                            target="_blank"
                            className="zentra-btn-ghost text-xs"
                            title="Vista que verá el cliente"
                        >
                            Ver como cliente
                        </Link>
                    )}
                </div>

                {property && (
                    <BulkMediaUpload
                        taskId={task.id}
                        folder={`zentra/cleanings/${(property.code || "misc").replace(/[^a-zA-Z0-9_-]/g, "_")}/${task.scheduled_date || "undated"}`}
                        onUploaded={load}
                    />
                )}

                {task.media?.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {task.media.map((m) => (
                            <a
                                key={m.id}
                                href={m.secure_url}
                                target="_blank"
                                rel="noreferrer"
                                className="block aspect-square rounded-md overflow-hidden border border-zentra-border bg-zentra-bg hover:border-zentra-primary transition relative group"
                                title={m.filename || m.label || m.type}
                                data-testid={`task-media-thumb-${m.id}`}
                            >
                                {String(m.resource_type || "").startsWith("video") || String(m.type || "").startsWith("video") ? (
                                    <>
                                        <video src={m.secure_url} className="w-full h-full object-cover" muted preload="metadata" />
                                        <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow">
                                            <Play size={24} />
                                        </span>
                                    </>
                                ) : (
                                    <img src={m.secure_url} alt={m.filename || ""} loading="lazy" className="w-full h-full object-cover" />
                                )}
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {/* Checklist */}
            <div className="zentra-card-static p-5" data-testid="task-checklist">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-semibold text-zentra-ink">Checklist operativa</h3>
                    <button className="zentra-btn-primary" onClick={saveChecklist} disabled={saving}>
                        <Save size={14} /> Guardar checklist
                    </button>
                </div>
                <ul className="space-y-2">
                    {(task.checklist || []).map((it, idx) => (
                        <li
                            key={idx}
                            className={`p-3 rounded-md border transition ${
                                it.done ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-zentra-border"
                            }`}
                            data-testid={`checklist-item-${idx}`}
                        >
                            <div className="flex items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => updateChecklistItem(idx, { done: !it.done })}
                                    className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center transition ${
                                        it.done
                                            ? "bg-zentra-accent border-zentra-accent text-white"
                                            : "bg-white border-zentra-border text-transparent"
                                    }`}
                                    data-testid={`checklist-toggle-${idx}`}
                                >
                                    <Check size={12} strokeWidth={3} />
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <p className="text-sm font-medium text-zentra-ink">
                                            {it.label}
                                            {it.required && <span className="text-red-600 ml-1">*</span>}
                                        </p>
                                        {it.photo_required && !it.photo && (
                                            <span className="zentra-chip bg-amber-100 text-amber-900 border-amber-200">
                                                <Camera size={10} className="mr-1" /> Foto obligatoria
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-end gap-3 flex-wrap">
                                        <PhotoUpload
                                            value={it.photo}
                                            onChange={(dataUrl) => updateChecklistItem(idx, { photo: dataUrl })}
                                            label="Evidencia"
                                            testId={`checklist-photo-${idx}`}
                                        />
                                        <textarea
                                            className="zentra-textarea text-sm flex-1 min-w-[200px] min-h-[60px]"
                                            value={it.note || ""}
                                            placeholder="Observación / nota…"
                                            onChange={(e) => updateChecklistItem(idx, { note: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
                {error && (
                    <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
                )}
            </div>
        </div>
    );
}
